import classNames from 'classnames';
import React from 'react';

import {Sequence as BioJSSequence} from 'biojs/Sequence';
import {Model as BioJSSequencePrimer} from 'biojs/SequencePrimer';
import {ASSEMBLY_TYPE} from 'constants/AssemblyConstants';
import {Enzyme} from 'molbio/enzymes/backbone/Enzymes';

export function isAssemblyPrimer(
  sequence: BioJSSequence,
  primer: BioJSSequencePrimer,
  assemblyType?: ASSEMBLY_TYPE
): boolean {
  const asm = require('assembly/Asm').default; // circular dependency

  return (
    asm.of(sequence).isFlankingPrimer(sequence, primer) &&
    (assemblyType == null || sequence.assemblyType() === assemblyType)
  );
}

interface BasePrimerBasesProps {
  baseMatchingStatuses: Array<boolean>;
  bases: string;
  isGoldenGate: boolean;
  overhangLength: number;
}

type PrimerBasesProps = BasePrimerBasesProps &
  (
    | {isGoldenGate: true; enzyme: Enzyme}
    | {
        isGoldenGate: false;
        enzyme?: never;
      }
  );

export default function PrimerBases({
  baseMatchingStatuses,
  bases,
  enzyme,
  isGoldenGate,
  overhangLength,
}: PrimerBasesProps): JSX.Element {
  let bindingRegionStart = 0;
  let postRecognitionSiteStart = 0;
  let recognitionSiteStart = 0;
  let stickyEndStart = 0;
  if (isGoldenGate) {
    recognitionSiteStart = bases.indexOf(enzyme.get('seq'));
    postRecognitionSiteStart = recognitionSiteStart + enzyme.recognitionSiteLength();
    stickyEndStart = postRecognitionSiteStart + enzyme.postRecognitionSiteLength();
    bindingRegionStart = stickyEndStart + enzyme.stickyEndLength();
  }

  return (
    <>
      {[...bases].map((base: string, index: number) => {
        const withinOverhang = index < overhangLength;
        const isMatch = baseMatchingStatuses[index];
        const isRecognitionSite =
          isGoldenGate && index >= recognitionSiteStart && index < postRecognitionSiteStart;
        const isStickyEnd = isGoldenGate && index >= stickyEndStart && index < bindingRegionStart;

        return (
          <span
            key={index}
            className={classNames({
              'primer-base-overhang': withinOverhang && !isRecognitionSite && !isStickyEnd,
              'primer-base-recognition-site': withinOverhang && isRecognitionSite,
              'primer-base-sticky-end': withinOverhang && isStickyEnd,
              'primer-base-match': !withinOverhang && isMatch,
              'primer-base-no-match': !withinOverhang && !isMatch,
            })}
          >
            {base}
          </span>
        );
      })}
    </>
  );
}
